import { Outlet, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { getCurrentProject } from "../../features/projects/state/projectsSlice";
import Navigation from "../../common/components/Navigation/Navigation";
import { useTranslation } from "react-i18next";
import AddTeamModal from "../../features/team/components/AddTeamModal";
import ParticipantCsv from "../../features/team/components/ParticipantFromCsvModal";
import { setAlert } from "../../features/alert/state/alertsSlice";
import OnboardingComponent from "../../common/util/OnBoardingComponent";
import useOnboardingSteps from "../../common/util/OnboardingTour";
import { getIsLoading } from "src/features/graph/state/graphuSlice";
import GraphIssuesBadge from "src/features/graph/components/GraphIssuesBadge";
import { jwtSelector } from "src/common/state/selectors/authSelector";
import { openPreviewInNewTab } from "src/common/util/openPreviewInNewTab";
import { getSelectedProjectGroup } from "src/features/projectGroups/state/projectGroupsSlice";

const Main = () => {
  const currentProject = useAppSelector(getCurrentProject);
  const isGraphLoading = useAppSelector(getIsLoading);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const jwt = useAppSelector(jwtSelector);
  const selectedProjectGroup = useAppSelector(getSelectedProjectGroup);
  const steps = useOnboardingSteps().onboardingHowToAddScene;

  // Every hook above runs on each render; only bail out once they have all been
  // called, so hook order stays identical render-to-render (Rules of Hooks).
  if (!currentProject) return null;

  const isAnonymousCourse = selectedProjectGroup.auth_type === "none";
  const shouldShowOnboarding = !isGraphLoading;
  const pathArray = pathname.split("/").filter(Boolean);

  const isInNodeDetailsPage =
    pathArray[0] === "courses" && pathArray[4] === "graph" && pathArray[5] === "node";

  const tabs = [
    { title: t("pages.project.tabs.scenario"), urlEndPoint: "graph" },
    {
      title: t("general.preview"),
      urlEndPoint: "preview",
      preventNavigation: true, // Story 13.15: Render as button, not NavLink
      // Story 13.15: Clicking Preview tab opens external window without navigation
      onClick: async () => {
        if (currentProject.id && jwt) {
          const result = await openPreviewInNewTab({
            projectId: currentProject.id,
            jwt,
            name: currentProject.name,
          });
          if (!result.success) {
            dispatch(
              setAlert({
                msg: t("alerts.previewError"),
                type: "warning",
              }),
            );
          }
        }
      },
    },
    { title: t("general.medias"), urlEndPoint: "medias" },
    {
      title: t("general.participants"),
      urlEndPoint: "learners",
      disabled: isAnonymousCourse,
      disabledTooltip: isAnonymousCourse ? t("pages.statistics.anonymousCourse") : undefined,
    },
    { title: t("general.information"), urlEndPoint: "information" },
  ];
  const revelantArticleIsPublished = "howToAddScene";
  return (
    <div className="flex flex-col h-full">
      <Navigation
        title={currentProject.name}
        isProjectView
        showGoBackButton
        // Module-health ⚠ pill: the issues it counts (nodes, translations,
        // unlinked scenes) are scenario-wide, so it belongs at title level.
        // Self-hides outside the Scénario tab (summary null when no graph).
        tagsBar={<GraphIssuesBadge />}
        tabs={tabs}
        backCustomRedirectTo={
          isInNodeDetailsPage ? `/${pathArray.slice(0, 5).join("/")}` : undefined
        }
      />
      {shouldShowOnboarding && (
        <OnboardingComponent
          id={revelantArticleIsPublished}
          relevantArticle={revelantArticleIsPublished}
          steps={steps}
        />
      )}
      {/* Story 13.15: Preview tab opens external window via onClick, no route navigation */}
      <div className="flex-1 min-h-0 flex flex-col">
        <Outlet />
      </div>
      <AddTeamModal />
      <ParticipantCsv />
    </div>
  );
};

export default Main;
